import React from 'react';
import styled from '@emotion/styled';
import { apiFetch } from '../utils/apiUtils';

const Container = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 1rem 0;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const DocumentTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DocumentTypeCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.borderColor};
`;

const DocumentTypeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const DocumentTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-weight: bold;
  color: white;
  font-size: 1.2rem;
`;

const DocumentTypeName = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ProcessingInfo = styled.div`
  margin-bottom: 1rem;
`;

const ProcessingLabel = styled.div`
  font-weight: 600;
  color: #555;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProcessingValue = styled.div`
  color: #333;
  font-size: 1rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid ${props => props.accentColor};
`;

const FileFormats = styled.div`
  margin-bottom: 1rem;
`;

const FileFormatsLabel = styled.div`
  font-weight: 600;
  color: #555;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FileFormatTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FileFormatTag = styled.span`
  background: ${props => props.backgroundColor};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const Description = styled.div`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
  font-style: italic;
`;

const SummarySection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 2rem;
`;

const SummaryTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  font-weight: 600;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const SummaryItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
`;

const SummaryNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color};
  margin-bottom: 0.5rem;
`;

const SummaryLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

function DocumentTypes() {
  const [documentTypes, setDocumentTypes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Fetch document types from API
  React.useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/system-files/document-types');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Transform API data to match the expected format
            const transformedTypes = data.document_types.map(docType => ({
              type: docType.display_name,
              icon: docType.display_name.charAt(0).toUpperCase(),
              borderColor: docType.color,
              backgroundColor: docType.color,
              accentColor: docType.color,
              processing: docType.document_style === 'form' ? 'Form OCR (if PDF)' : 'Letter OCR (Text)',
              formats: docType.document_style === 'form' 
                ? [
                    { name: 'CSV', color: '#27ae60' },
                    { name: 'PDF', color: '#e74c3c' }
                  ]
                : [
                    { name: 'PDF', color: '#e74c3c' },
                    { name: 'TXT', color: '#f39c12' }
                  ],
              description: `${docType.display_name} documents. ${docType.document_style === 'form' 
                ? 'CSV files processed as-is, PDF files use form-based OCR extraction.'
                : 'Always processed as letter-type documents with text extraction.'
              }`
            }));
            setDocumentTypes(transformedTypes);
          } else {
            setError('Failed to load document types');
          }
        } else {
          setError('Failed to fetch document types from server');
        }
      } catch (err) {
        console.error('Error fetching document types:', err);
        setError('Error loading document types');
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentTypes();
  }, []);

  // Calculate dynamic summary stats
  const summaryStats = React.useMemo(() => {
    const formTypes = documentTypes.filter(dt => dt.processing.includes('Form')).length;
    const letterTypes = documentTypes.filter(dt => dt.processing.includes('Letter')).length;
    const totalTypes = documentTypes.length;
    const totalFormats = documentTypes.reduce((sum, dt) => sum + dt.formats.length, 0);
    
    return [
      { number: formTypes.toString(), label: 'Form OCR Types', color: '#8b5cf6' },
      { number: letterTypes.toString(), label: 'Letter OCR Types', color: '#10b981' },
      { number: totalTypes.toString(), label: 'Total Document Types', color: '#3b82f6' },
      { number: totalFormats.toString(), label: 'Supported Formats', color: '#f59e0b' }
    ];
  }, [documentTypes]);

  if (loading) {
    return (
      <Container>
        <Title>Document Processing Configuration</Title>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          color: '#6b7280',
          fontSize: '1rem'
        }}>
          Loading document types...
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>Document Processing Configuration</Title>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          color: '#dc2626',
          fontSize: '1rem',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ marginBottom: '0.5rem' }}>⚠️ Error loading document types</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{error}</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Document Processing Configuration</Title>
      
      <DocumentTypeGrid>
        {documentTypes.map((docType, index) => (
          <DocumentTypeCard key={index} borderColor={docType.borderColor}>
            <DocumentTypeHeader>
              <DocumentTypeIcon backgroundColor={docType.backgroundColor}>
                {docType.icon}
              </DocumentTypeIcon>
              <DocumentTypeName>{docType.type}</DocumentTypeName>
            </DocumentTypeHeader>
            
            <ProcessingInfo>
              <ProcessingLabel>OCR Processing Style</ProcessingLabel>
              <ProcessingValue accentColor={docType.accentColor}>
                {docType.processing}
              </ProcessingValue>
            </ProcessingInfo>
            
            <FileFormats>
              <FileFormatsLabel>Supported File Formats</FileFormatsLabel>
              <FileFormatTags>
                {docType.formats.map((format, formatIndex) => (
                  <FileFormatTag 
                    key={formatIndex} 
                    backgroundColor={format.color}
                  >
                    {format.name}
                  </FileFormatTag>
                ))}
              </FileFormatTags>
            </FileFormats>
            
            <Description>
              {docType.description}
            </Description>
          </DocumentTypeCard>
        ))}
      </DocumentTypeGrid>
      
      <SummarySection>
        <SummaryTitle>Processing Summary</SummaryTitle>
        <SummaryGrid>
          {summaryStats.map((stat, index) => (
            <SummaryItem key={index}>
              <SummaryNumber color={stat.color}>{stat.number}</SummaryNumber>
              <SummaryLabel>{stat.label}</SummaryLabel>
            </SummaryItem>
          ))}
        </SummaryGrid>
      </SummarySection>
    </Container>
  );
}

export default DocumentTypes;
